import React, { useState } from "react";
import styled from "styled-components";
import { Dropdown, Menu, Button, Tooltip, Tag } from "antd";
import { MoreOutlined, SearchOutlined, InfoCircleOutlined, TagOutlined, CheckCircleOutlined, UserAddOutlined, FileAddOutlined, FileTextOutlined } from "@ant-design/icons";
import { toast } from "react-hot-toast";
import logger, { Logger } from '../../utils/logger';
import { hasFullAdminAccess } from '../../utils/roleUtils';

// Redux imports
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  selectSelectedConversation,
  deleteConversation,
  clearSelectedConversation
} from "../../store/slices/conversationsSlice";
import {
  selectLead
} from "../../store/slices/messagesSlice";
import {
  setShowChatDetails,
  selectShowChatDetails,
  setShowAssignLeadDrawer,
  setShowAssignChatDrawer,
  setOpenForm,
  setShowLabelPopup,
  setShowStatusPopup
} from "../../store/slices/uiSlice";
import {
  selectWhatsappPhoneId
} from "../../store/slices/userSlice";

// Import components
import { Info, FilePlus, FileText, CheckCircle, UserPlus, MoreVertical, RefreshCcw, Trash2, ShieldBan, ShieldAlert, AlertOctagon } from "lucide-react";
import StatusLabelChips from "./components/StatusLabelChips";
import InitialsAvatar from "./components/InitialsAvatar";
import GenericConfirmationModal from "../../components/GenericConfirmationModal";

// Import WhatsApp services
import { blockUnblockContact } from "../../services/whatsapp";
import { updateLeadLabel } from "../../services/leadService";

// Styled components - Modern Chat UI
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  width: 100%;
  border-bottom: 1px solid #e9ecef;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  backdrop-filter: blur(10px);
  min-height: 76px;
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  h2 {
    font-size: 17px;
    margin: 0;
    line-height: 1.3;
    font-family: "Gilroy-Bold";
    color: #1a1a1a;
    font-weight: 700;
  }

  p {
    font-size: 13px;
    color: #6c757d;
    margin: 0;
    font-family: "Gilroy-Medium";
    font-weight: 500;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IconButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  color: #495057;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;

  &:hover {
    background-color: #f8f9fa;
    color: #0084ff;
    border-color: #0084ff;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 132, 255, 0.18);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;




const HeaderButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0;
  
  &:hover {
    background-color: #f0f2f5;
  }
`;

interface InboxChatHeadReduxProps {
  setSearchInChat?: (search: boolean) => void;
  addTaskHandler?: () => void;
  addNoteHandler?: () => void;
  onLeadDetailsClick?: () => void;
  onRefresh?: () => void; // Add refresh prop
}

const InboxChatHeadRedux: React.FC<InboxChatHeadReduxProps> = ({
  setSearchInChat,
  addTaskHandler,
  addNoteHandler,
  onLeadDetailsClick, // Receive the prop
  onRefresh

}) => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const selectedConversation = useAppSelector(selectSelectedConversation);
  const showUserDetails = useAppSelector(selectShowChatDetails);
  const lead = useAppSelector(selectLead);
  const [from, setFrom] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockAction, setBlockAction] = useState<'block' | 'unblock'>('block');

  // Get phone number ID from WhatsApp integration details
  const phoneNumberId = useAppSelector(selectWhatsappPhoneId);

  // Check if contact is blocked (fallback to false if not available from backend)
  const isContactBlocked = selectedConversation?.isBlocked || false;
  
  // Access userPreferences correctly to get both labels and status
  const userPreferences = useAppSelector(state => {
    const preferences = state.rootReducers?.user?.userPreferences;

    if (!preferences || typeof preferences !== 'object') {
      return { labels: [], status: [] };
    }

    return {
      labels: (preferences as any).labels || [],
      status: (preferences as any).status || []
    };
  });

  // Get user details for access control
  const userDetails = useAppSelector((state: any) => state.rootReducers?.userData?.userDetails);



  // Debug logging
  Logger.log("InboxChatHeadRedux rendered", {
    conversationId: selectedConversation?.id,
    showUserDetails
  });

  // Event handlers
  const handleSearchClick = () => {
    if (setSearchInChat) {
      setSearchInChat(true);
    }
  };

  const handleShowDetails = () => {
    dispatch(setShowChatDetails(!showUserDetails));
  };

  const handleAddTask = () => {
    if (addTaskHandler) {
      addTaskHandler();
    } else {
      dispatch(setOpenForm("task"));
    }
  };

  const handleAddNote = () => {
    if (addNoteHandler) {
      addNoteHandler();
    } else {
      dispatch(setOpenForm("note"));
    }
  };

  const handleOpenLabels = () => {
    if (selectedConversation) {
      // Use the label preferences
      const labelPreferences = userPreferences?.labels || [];

      // Map and check which ones are already applied to this conversation
      const labelArray = labelPreferences.map((labelObj: any) => ({
        ...labelObj,
        isLabelChecked: (selectedConversation.label || []).includes(labelObj.value) || false
      }));

      // Set the from value to know where we're coming from
      setFrom("chat");

      // Set the label data first
      dispatch({
        type: 'SET_LEAD_LABEL_DATA',
        payload: labelArray
      });

      // Then open the popup
      dispatch(setShowLabelPopup(true));
    }
  };

  const handleOpenStatus = () => {
    dispatch(setShowStatusPopup(true));
  };

  const handleAssignLead = () => {
    if (!lead) return;
    
    dispatch(setShowAssignLeadDrawer(true));
  };

  const handleAssignChat = () => {
    if (!selectedConversation) return;

    dispatch(setShowAssignChatDrawer(true));
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation?._id) {
      toast.error("No conversation selected");
      return;
    }

    try {
      Logger.log('Deleting conversation:', selectedConversation._id);
      await dispatch(deleteConversation(selectedConversation._id)).unwrap();

      // Clear selection after successful delete
      dispatch(clearSelectedConversation());

      toast.success("Conversation deleted successfully");
      Logger.log('Conversation deleted successfully');
    } catch (error) {
      Logger.log('Failed to delete conversation:', error);
      toast.error("Failed to delete conversation");
    }
  };

  const handleBlockUnblock = async () => {
    if (!lead?._id) {
      toast.error("No lead associated with this conversation");
      return;
    }

    if (!phoneNumberId) {
      toast.error("Phone number ID not found. Please check your WhatsApp business settings.");
      return;
    }

    const willBlock = blockAction === 'block';

    try {
      Logger.log(`${willBlock ? 'Blocking' : 'Unblocking'} contact:`, {
        leadId: lead._id,
        phoneNumberId
      });

      await blockUnblockContact(lead._id, willBlock, phoneNumberId);

      toast.success(`Contact ${willBlock ? 'blocked' : 'unblocked'} successfully`);
      Logger.log(`Contact ${willBlock ? 'blocked' : 'unblocked'} successfully`);

      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      Logger.log(`Failed to ${blockAction} contact:`, error);
      toast.error(error?.response?.data?.message || `Failed to ${blockAction} contact`);
    }
  };

  // Dropdown menu
  const menu = (
    <Menu
      style={{
        width: '220px',  // Wider menu
        padding: '8px 0',
        borderRadius: '12px',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)'
      }}
    >
      <Menu.Item 
        key="view-profile" 
        icon={<Info size={18} />} 
        onClick={() => {
          logger.log('Menu clicked: View/Hide Details');
          handleShowDetails();
        }}
        style={{ padding: '12px 16px', fontSize: '14px' }}  // Larger padding
      >
        {showUserDetails ? "Hide Details" : "View Details"}
      </Menu.Item>
      
      <Menu.Divider style={{ margin: '8px 0' }} />
      
      <Menu.Item 
        key="add-task" 
        icon={<FilePlus size={18} />} 
        onClick={() => {
          logger.log('Menu clicked: Add Task');
          handleAddTask();
        }}
        style={{ padding: '12px 16px', fontSize: '14px' }}
      >
        Add Task
      </Menu.Item>
      
      <Menu.Item 
        key="add-note" 
        icon={<FileText size={18} />} 
        onClick={() => {
          logger.log('Menu clicked: Add Note');
          handleAddNote();
        }}
        style={{ padding: '12px 16px', fontSize: '14px' }}
      >
        Add Note
      </Menu.Item>
      
      <Menu.Divider style={{ margin: '8px 0' }} />
      
    
      
      
      
      
      
   
    </Menu>
  );

  // If no selected conversation, don't render anything
  if (!selectedConversation) {
    return null;
  }

  return (
    <HeaderContainer>
      <ProfileSection>
        <InitialsAvatar
          name={selectedConversation.name}
          phone={selectedConversation.mobile}
          size={40}
        />
        <UserInfo>
          <h2>{selectedConversation.name || "Unknown"}</h2>
          <p>{selectedConversation.mobile}</p>

          {/* Status and Label Chips */}
          {((selectedConversation?.status?.length ?? 0) > 0 || (selectedConversation?.label?.length ?? 0) > 0) && (
            <StatusLabelChips
              status={selectedConversation?.status}
              labels={selectedConversation?.label}
              userPreferences={userPreferences}
              maxVisible={4}
              compact={false}
            />
          )}
        </UserInfo>
      </ProfileSection>
      
      <ActionButtons>
        <Tooltip title="Lead Details" placement="bottom">
          <IconButton
            type="text"
            onClick={() => {
              logger.log('Clicked: View Lead Details');
              onLeadDetailsClick && onLeadDetailsClick();
            }}
          >
            <Info size={24} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Add Note" placement="bottom">
          <IconButton
            type="text"
            onClick={() => {
              logger.log('Clicked: Add Note');
              handleAddNote();
            }}
          >
            <FileText size={24} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Refresh" placement="bottom">
          <IconButton
            type="text"
            onClick={() => {
              logger.log('Clicked: Refresh');
              onRefresh && onRefresh();
            }}
          >
            <RefreshCcw size={24} />
          </IconButton>
        </Tooltip>

        {/* Block/Unblock button */}
        <Tooltip
          title={isContactBlocked ? "Unblock Contact" : "Block & Spam"}
          placement="bottom"
        >
          <IconButton
            type="text"
            onClick={() => {
              logger.log('Clicked: Block/Unblock Contact');
              setBlockAction(isContactBlocked ? 'unblock' : 'block');
              setShowBlockModal(true);
            }}
            style={{
              borderColor: isContactBlocked ? '#d4edda' : '#ffebee'
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
              if (isContactBlocked) {
                e.currentTarget.style.background = '#d4edda';
                e.currentTarget.style.borderColor = '#28a745';
                e.currentTarget.style.color = '#155724';
              } else {
                e.currentTarget.style.background = '#ffebee';
                e.currentTarget.style.borderColor = '#f44336';
                e.currentTarget.style.color = '#c62828';
              }
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.borderColor = '#e9ecef';
              e.currentTarget.style.color = '#495057';
            }}
          >
            {isContactBlocked ? <ShieldAlert size={24} /> : <AlertOctagon size={24} />}
          </IconButton>
        </Tooltip>

        {/* Delete button - Only visible to admins */}
        {hasFullAdminAccess(userDetails) && (
          <Tooltip title="Delete Conversation" placement="bottom">
            <IconButton
              type="text"
              onClick={() => {
                logger.log('Clicked: Delete Conversation');
                setShowDeleteModal(true);
              }}
              style={{
                borderColor: '#ffebee'
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                e.currentTarget.style.background = '#ffebee';
                e.currentTarget.style.borderColor = '#ef5350';
                e.currentTarget.style.color = '#d32f2f';
              }}
              onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.borderColor = '#e9ecef';
                e.currentTarget.style.color = '#495057';
              }}
            >
              <Trash2 size={24} />
            </IconButton>
          </Tooltip>
        )}
      </ActionButtons>

      {/* Delete Confirmation Modal */}
      <GenericConfirmationModal
        showModal={showDeleteModal}
        toggleModal={() => setShowDeleteModal(false)}
        onConfirmation={handleDeleteConversation}
        title="Delete Conversation"
        message={`Are you sure you want to delete this conversation with ${selectedConversation?.name || 'Unknown'}?\n\nThis action cannot be undone and will permanently remove all messages.`}
        confirmText="Delete"
        cancelText="Cancel"
        isWarning={true}
      />

      {/* Block/Unblock Confirmation Modal */}
      <GenericConfirmationModal
        showModal={showBlockModal}
        toggleModal={() => setShowBlockModal(false)}
        onConfirmation={handleBlockUnblock}
        title={blockAction === 'block' ? "Block Contact" : "Unblock Contact"}
        message={
          blockAction === 'block'
            ? `Are you sure you want to block ${selectedConversation?.name || 'this contact'}?`
            : `Are you sure you want to unblock ${selectedConversation?.name || 'this contact'}?`
        }
        confirmText={blockAction === 'block' ? "Block" : "Unblock"}
        cancelText="Cancel"
        isWarning={blockAction === 'block'}
      />
    </HeaderContainer>
  );
};

export default InboxChatHeadRedux;